/**
 * Traducción entre las filas de Postgres (snake_case) y los tipos del
 * frontend (camelCase). Ningún componente ni tipo cambia: todo el mapeo
 * vive aquí.
 */

import {
  Form,
  FormAnswer,
  FormQuestion,
  Invitation,
  LandingBlock,
  LandingPage,
  Lead,
  LeadFile,
  LeadNote,
  LeadSource,
  LeadStatusHistory,
  NoteColor,
  Organization,
  OrganizationMember,
  PipelineStage,
  Role,
  SavedComponent,
  User,
} from '../types'
import { slugify } from '../utils/format'

export type Row = Record<string, any>

/* ------------------------------------------------------------------ */
/* Usuarios y organizaciones                                           */
/* ------------------------------------------------------------------ */

export function toUser(row: Row): User {
  return {
    id: row.id,
    firstName: row.first_name ?? '',
    lastName: row.last_name ?? '',
    email: row.email ?? '',
    avatarUrl: row.avatar_url ?? undefined,
    createdAt: row.created_at ?? new Date().toISOString(),
  }
}

export function toOrganization(row: Row): Organization {
  return {
    id: row.id,
    name: row.name ?? '',
    sector: row.sector ?? '',
    slug: row.slug ?? '',
    logoUrl: row.logo_url ?? undefined,
    email: row.email ?? undefined,
    phone: row.phone ?? undefined,
    website: row.website ?? undefined,
    address: row.address ?? undefined,
    timezone: row.timezone ?? 'Europe/Madrid',
    brand: {
      primaryColor: row.primary_color ?? '#3D8BFF',
      secondaryColor: row.secondary_color ?? '#0B1F4D',
      fontFamily: row.font_family ?? 'Inter',
      logoUrl: row.logo_url ?? undefined,
      faviconUrl: row.favicon_url ?? undefined,
      buttonColor: row.button_color ?? undefined,
      textColor: row.text_color ?? undefined,
      backgroundColor: row.background_color ?? undefined,
      secondaryFontFamily: row.secondary_font_family ?? undefined,
      domain: row.domain ?? undefined,
    },
    privacy: {
      privacyPolicyUrl: row.privacy?.privacyPolicyUrl ?? '',
      legalNoticeUrl: row.privacy?.legalNoticeUrl ?? '',
      consentText:
        row.privacy?.consentText ??
        'Acepto la política de privacidad y autorizo que contacten conmigo.',
      cookiesText: row.privacy?.cookiesText ?? '',
    },
    notifications: {
      newLead: row.notifications?.newLead ?? true,
      leadAssigned: row.notifications?.leadAssigned ?? true,
      statusChange: row.notifications?.statusChange ?? true,
      newMeeting: row.notifications?.newMeeting ?? true,
    },
    createdAt: row.created_at ?? new Date().toISOString(),
  }
}

/** Convierte un parche del frontend en columnas de `organizations`. */
export function organizationPatch(patch: Partial<Organization>): Row {
  const row: Row = {}
  if (patch.name !== undefined) row.name = patch.name
  if (patch.sector !== undefined) row.sector = patch.sector
  if (patch.slug !== undefined) row.slug = patch.slug
  if (patch.email !== undefined) row.email = patch.email
  if (patch.phone !== undefined) row.phone = patch.phone
  if (patch.website !== undefined) row.website = patch.website
  if (patch.address !== undefined) row.address = patch.address
  if (patch.timezone !== undefined) row.timezone = patch.timezone
  if (patch.privacy !== undefined) row.privacy = patch.privacy
  if (patch.notifications !== undefined) row.notifications = patch.notifications
  if (patch.brand) {
    const brand = patch.brand
    if (brand.primaryColor !== undefined) row.primary_color = brand.primaryColor
    if (brand.secondaryColor !== undefined)
      row.secondary_color = brand.secondaryColor
    if (brand.buttonColor !== undefined) row.button_color = brand.buttonColor
    if (brand.textColor !== undefined) row.text_color = brand.textColor
    if (brand.backgroundColor !== undefined)
      row.background_color = brand.backgroundColor
    if (brand.fontFamily !== undefined) row.font_family = brand.fontFamily
    if (brand.secondaryFontFamily !== undefined)
      row.secondary_font_family = brand.secondaryFontFamily
    if (brand.domain !== undefined) row.domain = brand.domain
    if (brand.logoUrl !== undefined) row.logo_url = brand.logoUrl
    if (brand.faviconUrl !== undefined) row.favicon_url = brand.faviconUrl
  }
  if (patch.logoUrl !== undefined) row.logo_url = patch.logoUrl
  row.updated_at = new Date().toISOString()
  return row
}

export function toMember(row: Row): OrganizationMember {
  const profile = row.profiles ?? {}
  return {
    id: row.id,
    organizationId: row.organization_id,
    userId: row.user_id,
    role: (row.role ?? 'comercial') as Role,
    active: row.status !== 'disabled',
    firstName: profile.first_name ?? '',
    lastName: profile.last_name ?? '',
    email: profile.email ?? '',
    avatarUrl: profile.avatar_url ?? undefined,
    createdAt: row.created_at ?? new Date().toISOString(),
  }
}

const INVITATION_STATUS: Record<string, Invitation['status']> = {
  pending: 'pendiente',
  accepted: 'aceptada',
  cancelled: 'cancelada',
  canceled: 'cancelada',
  expired: 'cancelada',
}

export function toInvitation(row: Row): Invitation {
  return {
    id: row.id,
    organizationId: row.organization_id,
    email: row.email,
    role: (row.role ?? 'comercial') as Role,
    status: INVITATION_STATUS[row.status] ?? 'pendiente',
    createdAt: row.created_at ?? new Date().toISOString(),
  }
}

/* ------------------------------------------------------------------ */
/* Embudo y fuentes                                                    */
/* ------------------------------------------------------------------ */

export function toStage(row: Row): PipelineStage {
  return {
    id: row.id,
    organizationId: row.organization_id,
    name: row.name,
    color: row.color ?? '#3D8BFF',
    order: row.position ?? 0,
    active: row.is_active !== false,
    isInitial: row.is_initial === true,
  }
}

export function stagePatch(patch: Partial<PipelineStage>): Row {
  const row: Row = {}
  if (patch.name !== undefined) row.name = patch.name
  if (patch.color !== undefined) row.color = patch.color
  if (patch.order !== undefined) row.position = patch.order
  if (patch.active !== undefined) row.is_active = patch.active
  if (patch.isInitial !== undefined) row.is_initial = patch.isInitial
  return row
}

export function toSource(row: Row, index = 0): LeadSource {
  return {
    id: row.id,
    organizationId: row.organization_id,
    key: slugify(row.name ?? ''),
    name: row.name ?? '',
    icon: row.icon ?? 'Globe',
    color: row.color ?? '#5F6673',
    active: row.is_active !== false,
    order: index + 1,
  }
}

export function sourcePatch(patch: Partial<LeadSource>): Row {
  const row: Row = {}
  if (patch.name !== undefined) row.name = patch.name
  if (patch.icon !== undefined) row.icon = patch.icon
  if (patch.color !== undefined) row.color = patch.color
  if (patch.active !== undefined) row.is_active = patch.active
  return row
}

/* ------------------------------------------------------------------ */
/* Leads                                                               */
/* ------------------------------------------------------------------ */

export function splitName(fullName: string): [string, string] {
  const parts = (fullName ?? '').trim().split(/\s+/)
  if (parts.length <= 1) return [parts[0] ?? '', '']
  return [parts[0], parts.slice(1).join(' ')]
}

export function toLead(
  row: Row,
  sourceName: (id?: string) => string,
  answers: FormAnswer[] = [],
): Lead {
  const [firstName, lastName] = splitName(row.full_name ?? '')
  return {
    id: row.id,
    organizationId: row.organization_id,
    firstName,
    lastName,
    company: row.company ?? '',
    email: row.email ?? '',
    phone: row.phone ?? '',
    website: row.website ?? undefined,
    instagram: row.instagram ?? undefined,
    city: row.city ?? undefined,
    country: row.country ?? undefined,
    service: row.service_type ?? undefined,
    adBudget: row.ad_investment ?? undefined,
    budget: row.budget ?? undefined,
    stageId: row.stage_id ?? '',
    assignedTo: row.assigned_to ?? undefined,
    origin: {
      source: sourceName(row.source_id ?? undefined) || row.utm_source || 'directo',
      medium: row.utm_medium ?? '',
      campaign: row.utm_campaign ?? '',
      content: row.utm_content ?? '',
      term: row.utm_term ?? '',
      landingId: row.landing_id ?? undefined,
      formId: row.form_id ?? undefined,
      referrer: '',
    },
    answers,
    createdAt: row.created_at ?? new Date().toISOString(),
    lastContactAt: row.last_contacted_at ?? undefined,
    deletedAt: row.deleted_at ?? undefined,
    deletedBy: row.deleted_by ?? undefined,
  }
}

/** Serializa el título de la nota junto al contenido sin perder datos. */
export function encodeNote(body: string, title?: string): string {
  return title ? JSON.stringify({ t: title, b: body }) : body
}

export function decodeNote(content: string): { title?: string; body: string } {
  if (typeof content === 'string' && content.startsWith('{"t":')) {
    try {
      const parsed = JSON.parse(content)
      return { title: parsed.t, body: parsed.b ?? '' }
    } catch {
      return { body: content }
    }
  }
  return { body: content ?? '' }
}

export function toNote(row: Row, authorName: (id: string) => string): LeadNote {
  const { title, body } = decodeNote(row.content)
  return {
    id: row.id,
    organizationId: row.organization_id,
    leadId: row.lead_id,
    authorId: row.author_id,
    authorName: authorName(row.author_id),
    title,
    body,
    color: (row.color ?? 'gray') as NoteColor,
    createdAt: row.created_at ?? new Date().toISOString(),
    updatedAt: row.updated_at ?? undefined,
    deletedAt: row.deleted_at ?? undefined,
  }
}

const ACTIVITY_TYPES: LeadStatusHistory['type'][] = [
  'creacion',
  'estado',
  'asignacion',
  'nota',
  'contacto',
]

export function toHistory(
  row: Row,
  authorName: (id: string) => string,
): LeadStatusHistory {
  return {
    id: row.id,
    organizationId: row.organization_id,
    leadId: row.lead_id,
    type: ACTIVITY_TYPES.includes(row.type) ? row.type : 'estado',
    description: row.description ?? '',
    authorName: authorName(row.created_by),
    createdAt: row.created_at ?? new Date().toISOString(),
  }
}

export function toFile(row: Row, url: string): LeadFile {
  return {
    id: row.id,
    organizationId: row.organization_id,
    leadId: row.lead_id,
    name: row.name,
    size: row.size ?? 0,
    type: row.type ?? '',
    url,
    uploadedBy: row.uploaded_by ?? '',
    createdAt: row.created_at ?? new Date().toISOString(),
  }
}

/* ------------------------------------------------------------------ */
/* Formularios                                                         */
/* ------------------------------------------------------------------ */

export function toQuestion(row: Row): FormQuestion {
  const options: string[] = Array.isArray(row.options)
    ? row.options.map((o: any) => (typeof o === 'string' ? o : o?.label ?? ''))
    : []
  const extra = (row.conditional_logic ?? {}) as Row
  return {
    id: row.id,
    formId: row.form_id,
    label: row.question ?? '',
    description: row.description ?? undefined,
    type: row.type,
    options,
    required: row.is_required === true,
    order: row.position ?? 0,
    placeholder: row.placeholder ?? undefined,
    optionDescriptions: extra.optionDescriptions ?? undefined,
    crmField: extra.crmField ?? undefined,
    crmValues: extra.crmValues ?? undefined,
    fieldKey: row.field_key ?? undefined,
    conditionalLogic: {
      enabled: extra.enabled === true,
      questionId: extra.questionId ?? null,
      operator: extra.operator ?? 'igual',
      value: extra.value ?? '',
    },
    isActive: extra.isActive !== false,
  }
}

/** Columna `field_key` a partir del destino en el CRM. */
const CRM_FIELD_KEYS: Record<string, string> = {
  service: 'service_type',
  adBudget: 'ad_investment',
  budget: 'budget',
  city: 'city',
  instagram: 'instagram',
}

export function questionRow(
  question: FormQuestion,
  formId: string,
  organizationId: string,
): Row {
  return {
    id: question.id,
    form_id: formId,
    organization_id: organizationId,
    type: question.type,
    question: question.label,
    description: question.description ?? null,
    placeholder: question.placeholder ?? null,
    is_required: question.required,
    options: question.options,
    field_key: question.crmField
      ? (CRM_FIELD_KEYS[question.crmField] ?? null)
      : null,
    position: question.order,
    // `form_questions` no tiene columna `is_active`: el estado de la pregunta
    // y sus metadatos viajan dentro de `conditional_logic` (jsonb).
    conditional_logic: {
      ...question.conditionalLogic,
      isActive: question.isActive !== false,
      optionDescriptions: question.optionDescriptions ?? null,
      crmField: question.crmField ?? null,
      crmValues: question.crmValues ?? null,
    },
  }
}

export function toForm(row: Row, config: Row, questions: FormQuestion[]): Form {
  return {
    id: row.id,
    organizationId: row.organization_id,
    name: row.name,
    description: config.description ?? undefined,
    slug: row.slug,
    status: row.deleted_at
      ? config.status ?? 'borrador'
      : row.is_active
        ? 'activo'
        : (config.status === 'inactivo' ? 'inactivo' : 'borrador'),
    submitLabel: config.submitLabel ?? 'Enviar',
    successMessage: row.success_message ?? 'Hemos recibido tu solicitud.',
    thankYouSlug: config.thankYouSlug ?? undefined,
    redirectUrl: row.redirect_url ?? undefined,
    contactFields: config.contactFields ?? [],
    contactPosition: config.contactPosition ?? 'inicio',
    contactTitle: config.contactTitle ?? undefined,
    privacyNotice: config.privacyNotice ?? undefined,
    consentText: config.consentText ?? '',
    questions: questions.sort((a, b) => a.order - b.order),
    createdAt: row.created_at ?? new Date().toISOString(),
    updatedAt: row.updated_at ?? new Date().toISOString(),
    deletedAt: row.deleted_at ?? undefined,
  }
}

/** Datos del formulario que no tienen columna propia en `forms`. */
export function formConfig(form: Form): Row {
  return {
    description: form.description ?? null,
    status: form.status,
    submitLabel: form.submitLabel,
    thankYouSlug: form.thankYouSlug ?? null,
    contactFields: form.contactFields,
    contactPosition: form.contactPosition ?? 'inicio',
    contactTitle: form.contactTitle ?? null,
    privacyNotice: form.privacyNotice ?? null,
    consentText: form.consentText,
  }
}

export function formRow(form: Form): Row {
  return {
    id: form.id,
    organization_id: form.organizationId,
    name: form.name,
    slug: form.slug,
    is_active: form.status === 'activo',
    success_message: form.successMessage,
    success_subtext: form.description ?? null,
    redirect_url: form.redirectUrl ?? null,
    deleted_at: form.deletedAt ?? null,
  }
}

/* ------------------------------------------------------------------ */
/* Landings                                                            */
/* ------------------------------------------------------------------ */

export function toBlock(row: Row): LandingBlock {
  const style = (row.style ?? {}) as Row
  return {
    id: row.id,
    type: row.type,
    visible: row.is_visible !== false,
    spacing: style.spacing ?? 'normal',
    config: (row.content ?? {}) as Record<string, unknown>,
    style: style.block ?? undefined,
    button: style.button ?? undefined,
  }
}

export function blockRow(
  block: LandingBlock,
  landingId: string,
  organizationId: string,
  position: number,
): Row {
  return {
    id: block.id,
    landing_id: landingId,
    organization_id: organizationId,
    type: block.type,
    position,
    is_visible: block.visible,
    content: block.config,
    style: {
      spacing: block.spacing,
      block: block.style ?? null,
      button: block.button ?? null,
    },
  }
}

export function toLanding(
  row: Row,
  config: Row,
  blocks: LandingBlock[],
): LandingPage {
  return {
    id: row.id,
    organizationId: row.organization_id,
    formId: row.form_id ?? undefined,
    name: row.name,
    slug: row.slug,
    status:
      row.status === 'published'
        ? 'publicada'
        : config.status === 'despublicada'
          ? 'despublicada'
          : 'borrador',
    pageConfig: {
      theme: config.theme ?? 'blanco_premium',
      primaryColor: config.primaryColor ?? '#3D8BFF',
      fontFamily: config.fontFamily ?? 'Inter',
      logoUrl: config.logoUrl ?? undefined,
      blocks,
      thankYou: config.thankYou ?? {
        title: 'Hemos recibido tu solicitud.',
        text: 'Nuestro equipo se pondrá en contacto contigo lo antes posible.',
        showCalendly: false,
        showWhatsapp: false,
        showHome: true,
      },
    },
    views: config.views ?? 0,
    createdAt: row.created_at ?? new Date().toISOString(),
    updatedAt: row.updated_at ?? new Date().toISOString(),
    deletedAt: row.deleted_at ?? undefined,
  }
}

export function landingConfig(landing: LandingPage): Row {
  const { blocks, ...rest } = landing.pageConfig
  return { ...rest, status: landing.status, views: landing.views }
}

export function landingRow(landing: LandingPage): Row {
  return {
    id: landing.id,
    organization_id: landing.organizationId,
    name: landing.name,
    slug: landing.slug,
    status: landing.status === 'publicada' ? 'published' : 'draft',
    form_id: landing.formId ?? null,
    published_at: landing.status === 'publicada' ? new Date().toISOString() : null,
    deleted_at: landing.deletedAt ?? null,
  }
}

export function toSavedComponent(row: Row): SavedComponent {
  return {
    id: row.id,
    organizationId: row.organization_id,
    name: row.name,
    category: row.type ?? 'bloque',
    block: {
      ...(row.content as LandingBlock),
      style: row.style?.block ?? (row.content as LandingBlock)?.style,
    },
    createdAt: row.created_at ?? new Date().toISOString(),
  }
}
